import React, { useEffect, useState } from "react";
import { Building2 } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Company (org-level) goals — STAFF VIEW, READ ONLY.
//
// Source: GET /api/performance/org-goals/visible, which returns ONLY
// type == "ORG" goals for the caller's org (filtered server-side).
// Department and team goals are never served by that endpoint and are
// not requested here.
//
// Read-only by construction: this component renders text and a progress
// bar. There is no create / edit / delete control and no mutating fetch —
// employees can SEE the company's objectives, not change them. Editing
// stays on the admin surface (Admin > Performance > Goals).
//
// Self-hides when there are no company goals, matching the sibling cards
// on this page (MyPIPCard / MyCareerCard / MyTransferCard).
// ─────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const STATUS_STYLE = {
  ON_TRACK: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  AT_RISK:  "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  BEHIND:   "text-red-400 bg-red-500/10 border-red-500/20",
  DONE:     "text-white/30 bg-white/5 border-white/10",
};

const BAR_COLOR = {
  ON_TRACK: "#00ff90",
  AT_RISK:  "#facc15",
  BEHIND:   "#f87171",
  DONE:     "rgba(255,255,255,0.25)",
};

export default function CompanyGoals() {
  const [goals,   setGoals]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/performance/org-goals/visible`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setGoals(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setGoals([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className={`${card} p-5`}>
        <div className="h-4 w-40 rounded bg-white/5 animate-pulse mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-white/5 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Nothing set at company level — stay out of the way.
  if (goals.length === 0) return null;

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Company Goals</span>
        </div>
        <span className="text-xs text-white/25">{goals.length}</span>
      </div>

      <div className="p-5 space-y-3">
        {goals.map(g => {
          const pct    = Math.max(0, Math.min(100, Number(g.progress) || 0));
          const status = g.status || "ON_TRACK";
          return (
            <div key={g.id} className="rounded-xl border border-white/5 bg-white/3 p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-sm text-white/80 font-medium">{g.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[status] || STATUS_STYLE.ON_TRACK}`}>
                      {String(status).replace("_", " ")}
                    </span>
                  </div>
                  {g.description && <p className="text-xs text-white/35 mt-1">{g.description}</p>}
                  <div className="flex items-center gap-3 flex-wrap mt-1">
                    {g.owner    && <span className="text-xs text-white/30">Owner: {g.owner}</span>}
                    {g.due_date && <span className="text-xs text-white/20">Due: {g.due_date}</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-white/5">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: BAR_COLOR[status] || BAR_COLOR.ON_TRACK }} />
                </div>
                <span className="text-xs font-medium text-white/50 w-9 text-right">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
